import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "shipment-custom-create-root" }
const _hoisted_2 = { class: "shipment-custom-create-body" }
const _hoisted_3 = { class: "shipment-custom-create-grid" }
const _hoisted_4 = { class: "shipment-custom-create-field" }
const _hoisted_5 = { class: "shipment-custom-create-field" }
const _hoisted_6 = { class: "shipment-custom-create-field" }
const _hoisted_7 = { class: "shipment-custom-create-field" }
const _hoisted_8 = { class: "shipment-custom-create-field" }
const _hoisted_9 = { class: "shipment-custom-create-field" }
const _hoisted_10 = {
  key: 0,
  class: "shipment-custom-create-field"
}
const _hoisted_11 = { class: "shipment-custom-create-field shipment-custom-create-notes" }
const _hoisted_12 = {
  key: 0,
  class: "shipment-custom-create-message error"
}
const _hoisted_13 = {
  key: 1,
  class: "shipment-custom-create-message success"
}
const _hoisted_14 = { class: "shipment-custom-create-footer" }

import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/modules/firebase';
import {
  getCurrentUser,
  refreshCurrentUserProfile,
  USER_PROFILE_UPDATED_EVENT,
  type CachedUser
} from '@/modules/firebase/Auth';
import { Button, Input, Select, TextArea } from '@/components/ui';
import type { SelectOption } from '@/components/ui';

type CreateCustomShipmentResponse = {
  status: 'ok' | 'error';
  message?: string;
  tracking?: string;
};

interface CustomShipmentFormState {
  tracking: string;
  courier: string;
  type: string;
  direction: string;
  customer: string;
  referenceNo: string;
  rmaExpirationDate: string;
  notes: string;
}

interface CreateCustomShipmentPayload {
  tracking: string;
  courier: string;
  type: string;
  direction: string;
  customer?: string;
  referenceNo?: string;
  destination?: {
    city: string;
    state: string;
    country: string;
    countrycode: string;
    zip: string;
  };
  notes?: string;
  rmaExpiresAt?: number;
  rmaWindowDays?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ShipmentCustomCreateModal',
  setup(__props) {

const INBOUND_DESTINATION = {
  city: 'Waterloo',
  state: 'ON',
  country: 'Canada',
  countrycode: 'CA',
  zip: 'N2L 0C7'
};

const courierOptions: SelectOption[] = [
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'USPS', label: 'USPS' },
  { value: 'DHL', label: 'DHL' },
  { value: 'CEVA', label: 'CEVA' },
  { value: 'dayross', label: 'Day & Ross' }
];

const typeOptions: SelectOption[] = [
  { value: 'RMA', label: 'RMA' },
  { value: 'CSW', label: 'CSW' },
  { value: 'Purchasing', label: 'Purchasing' },
  { value: 'R&D', label: 'R&D' },
  { value: 'Other', label: 'Other' }
];

const directionOptions: SelectOption[] = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' }
];

const createCustomShipment = httpsCallable<CreateCustomShipmentPayload, CreateCustomShipmentResponse>(
  functions,
  'createCustomShipment'
);

const isOpen = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const currentUser = ref<CachedUser | null>(null);
const canCreateShipment = computed(() => currentUser.value?.permissions?.shipmentCreate === true);

function handleUserProfileUpdated(event: Event): void {
  currentUser.value = (event as CustomEvent<CachedUser>).detail;
}

const form = reactive<CustomShipmentFormState>(createInitialFormState());

const isSubmitDisabled = computed(() => {
  return isSubmitting.value
    || !form.tracking.trim()
    || !form.courier
    || !form.type
    || (form.type === 'RMA' && !form.rmaExpirationDate);
});

function createInitialFormState(): CustomShipmentFormState {
  return {
    tracking: '',
    courier: 'UPS',
    type: 'RMA',
    direction: 'inbound',
    customer: 'Voltera',
    referenceNo: '',
    rmaExpirationDate: getDateInputValue(30),
    notes: ''
  };
}

function resetForm(): void {
  Object.assign(form, createInitialFormState());
}

function getDateInputValue(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().slice(0, 10);
}

function dateInputToUnixSeconds(dateInput: string): number | undefined {
  if (!dateInput) {
    return undefined;
  }

  return Math.floor(new Date(`${dateInput}T23:59:59`).getTime() / 1000);
}

function openModal(): void {
  errorMessage.value = '';
  successMessage.value = '';
  isOpen.value = true;
}

function closeModal(): void {
  if (isSubmitting.value) {
    return;
  }

  isOpen.value = false;
  errorMessage.value = '';
  successMessage.value = '';
}

async function submitForm(): Promise<void> {
  if (isSubmitDisabled.value) {
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const isInbound = form.direction === 'inbound';
    const payload: CreateCustomShipmentPayload = {
      tracking: form.tracking.trim(),
      courier: form.courier,
      type: form.type,
      direction: form.direction,
      customer: isInbound ? 'Voltera' : form.customer.trim() || undefined,
      referenceNo: form.referenceNo.trim() || undefined,
      destination: isInbound ? INBOUND_DESTINATION : undefined,
      notes: form.notes.trim() || undefined,
      rmaExpiresAt: form.type === 'RMA' ? dateInputToUnixSeconds(form.rmaExpirationDate) : undefined,
      rmaWindowDays: 30
    };

    const response = await createCustomShipment(payload);
    const result = response.data;

    if (result.status !== 'ok') {
      errorMessage.value = result.message || 'Failed to create custom shipment';
      return;
    }

    successMessage.value = result.message || 'Custom shipment created';
    window.dispatchEvent(new CustomEvent('jidoka:custom-shipment-created', {
      detail: { tracking: result.tracking || form.tracking }
    }));
    resetForm();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to create custom shipment';
  } finally {
    isSubmitting.value = false;
  }
}

watch(() => form.type, (type) => {
  if (type === 'RMA' && !form.rmaExpirationDate) {
    form.rmaExpirationDate = getDateInputValue(30);
  }
});

watch(() => form.direction, (direction, previousDirection) => {
  if (direction === 'inbound') {
    form.customer = 'Voltera';
    return;
  }

  if (previousDirection === 'inbound' && form.customer === 'Voltera') {
    form.customer = '';
  }
});

onMounted(async () => {
  window.addEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);

  try {
    await refreshCurrentUserProfile();
    currentUser.value = getCurrentUser();
  } catch (error) {
    console.warn('[ShipmentCustomCreate] Unable to verify shipmentCreate permission:', error);
    currentUser.value = null;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (canCreateShipment.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          id: "shipmentCustomCreateTrigger",
          type: "button",
          class: "button pill -text shipment-custom-create-trigger",
          "aria-label": "Create custom shipment",
          "data-tippy-content": "Create custom shipment",
          "data-tippy-placement": "top",
          onClick: openModal
        }, [...(_cache[8] || (_cache[8] = [
          _createElementVNode("span", {
            class: "icon-plus-21 icon-grey",
            "aria-hidden": "true"
          }, null, -1 /* CACHED */)
        ]))]))
      : _createCommentVNode("v-if", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "shipment-custom-create-backdrop",
            onMousedown: _withModifiers(closeModal, ["self"])
          }, [
            _createElementVNode("form", {
              class: "shipment-custom-create-modal",
              onSubmit: _withModifiers(submitForm, ["prevent"])
            }, [
              _createElementVNode("header", { class: "shipment-custom-create-header" }, [
                _cache[10] || (_cache[10] = _createElementVNode("div", null, [
                  _createElementVNode("h2", null, "Create custom shipment"),
                  _createElementVNode("p", null, "Create an active shipment and sync it with the selected courier.")
                ], -1 /* CACHED */)),
                _createElementVNode("button", {
                  type: "button",
                  class: "shipment-custom-create-close",
                  "aria-label": "Close",
                  onClick: closeModal
                }, [...(_cache[9] || (_cache[9] = [
                  _createElementVNode("i", { class: "material-icons" }, "close", -1 /* CACHED */)
                ]))])
              ]),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("label", _hoisted_4, [
                    _cache[11] || (_cache[11] = _createElementVNode("span", null, "Tracking number", -1 /* CACHED */)),
                    _createVNode(_unref(Input), {
                      modelValue: form.tracking,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.tracking) = $event)),
                      variant: "form",
                      autocomplete: "off",
                      required: ""
                    }, null, 8 /* PROPS */, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_5, [
                    _cache[12] || (_cache[12] = _createElementVNode("span", null, "Courier", -1 /* CACHED */)),
                    _createVNode(_unref(Select), {
                      modelValue: form.courier,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.courier) = $event)),
                      options: courierOptions,
                      "aria-label": "Courier",
                      variant: "form"
                    }, null, 8 /* PROPS */, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_6, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "Type", -1 /* CACHED */)),
                    _createVNode(_unref(Select), {
                      modelValue: form.type,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.type) = $event)),
                      options: typeOptions,
                      "aria-label": "Custom shipment type",
                      variant: "form"
                    }, null, 8 /* PROPS */, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_7, [
                    _cache[14] || (_cache[14] = _createElementVNode("span", null, "Direction", -1 /* CACHED */)),
                    _createVNode(_unref(Select), {
                      modelValue: form.direction,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.direction) = $event)),
                      options: directionOptions,
                      "aria-label": "Direction",
                      variant: "form"
                    }, null, 8 /* PROPS */, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_8, [
                    _cache[15] || (_cache[15] = _createElementVNode("span", null, "Customer", -1 /* CACHED */)),
                    _createVNode(_unref(Input), {
                      modelValue: form.customer,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.customer) = $event)),
                      variant: "form",
                      autocomplete: "organization",
                      disabled: form.direction === 'inbound'
                    }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                  ]),
                  _createElementVNode("label", _hoisted_9, [
                    _cache[16] || (_cache[16] = _createElementVNode("span", null, "Reference", -1 /* CACHED */)),
                    _createVNode(_unref(Input), {
                      modelValue: form.referenceNo,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.referenceNo) = $event)),
                      variant: "form",
                      autocomplete: "off"
                    }, null, 8 /* PROPS */, ["modelValue"])
                  ]),
                  (form.type === 'RMA')
                    ? (_openBlock(), _createElementBlock("label", _hoisted_10, [
                        _cache[17] || (_cache[17] = _createElementVNode("span", null, "RMA label expiration", -1 /* CACHED */)),
                        _createVNode(_unref(Input), {
                          modelValue: form.rmaExpirationDate,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.rmaExpirationDate) = $event)),
                          variant: "form",
                          type: "date",
                          required: ""
                        }, null, 8 /* PROPS */, ["modelValue"])
                      ]))
                    : _createCommentVNode("v-if", true)
                ]),
                _createElementVNode("label", _hoisted_11, [
                  _cache[18] || (_cache[18] = _createElementVNode("span", null, "Creation note", -1 /* CACHED */)),
                  _createVNode(_unref(TextArea), {
                    modelValue: form.notes,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.notes) = $event)),
                    variant: "form",
                    rows: 3,
                    placeholder: "Optional context for the activity log"
                  }, null, 8 /* PROPS */, ["modelValue"])
                ]),
                (errorMessage.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(errorMessage.value), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true),
                (successMessage.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(successMessage.value), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("footer", _hoisted_14, [
                _createVNode(_unref(Button), {
                  type: "button",
                  variant: "ghost",
                  tone: "neutral",
                  disabled: isSubmitting.value,
                  onClick: closeModal
                }, {
                  default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                    _createTextVNode("Cancel", -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"]),
                _createVNode(_unref(Button), {
                  type: "submit",
                  variant: "solid",
                  tone: "primary",
                  disabled: isSubmitDisabled.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isSubmitting.value ? 'Creating...' : 'Create shipment'), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"])
              ])
            ], 32 /* NEED_HYDRATION */)
          ], 32 /* NEED_HYDRATION */))
        : _createCommentVNode("v-if", true)
    ]))
  ]))
}
}

})